import { useState, useEffect, useCallback, useRef, createContext, useContext, useMemo } from 'react';
import { getBrandsDO } from 'utils/brandsUtils';
import { loadSettings } from 'utils/settingsUtils';
import { Brand } from 'models/BO/Brand';
import { networkGlobals } from 'utils/networkUtils';
import { changeFavicon } from 'utils/globalsUtils';
import { useTranslations } from './useTranslations';

export interface BrandsState {
  brands: Brand[];
  allBrands: Brand[];
  currentBrand: Brand;
  isLoading: boolean;
  error: string | null;
}

export interface UseBrandsReturn extends BrandsState {
  setBrandById: (brandId: number) => Promise<void>;
  initializeBrands: () => Promise<void>;
  setBrands: (brandIds: number[]) => Promise<void>;
}

export const BrandsContext = createContext<UseBrandsReturn>({
  brands: [],
  allBrands: [],
  currentBrand: new Brand(),
  isLoading: false,
  error: null,
  setBrandById: () => Promise.resolve(),
  initializeBrands: () => Promise.resolve(),
  setBrands: () => Promise.resolve(),
});

export const useBrands = (): UseBrandsReturn => {
  return useContext<UseBrandsReturn>(BrandsContext);
};

export function BrandsProvider({ children }) {
  const loadedBrands = useMemo(() => ({}), []);
  const [state, setState] = useState<BrandsState>({
    brands: [],
    allBrands: [],
    currentBrand: new Brand(),
    isLoading: true,
    error: null,
  });

  const { loadTranslations } = useTranslations();
  const isInitialized = useRef(false);
  // Load brand-specific data (translations and settings)
  const loadBrandData = useCallback(async (brand: Brand) => {
    if (!brand) return;

    try {
      // Update network globals
      networkGlobals.brand = Object.assign(new Brand(), brand);
      
      // Change favicon
      changeFavicon(brand.name);
      

      // Check if brand data is already loaded
      if (loadedBrands[brand.id]) {
        console.log(`Brand data already loaded for: ${brand.name}`);
        return;
      }
      loadedBrands[brand.id] = true;
      // Load translations and settings in parallel
      await Promise.all([
        loadTranslations(brand),
        loadSettings()
      ]);


      console.log(`Brand data loaded for: ${brand.name}`);
    } catch (error) {
      console.error('Error loading brand data:', error);
      setState(prev => ({ ...prev, error: 'Failed to load brand data' }));
    }
  }, []); // No dependencies

  // Set brand by ID
  const setBrandById = useCallback(async (brandId: number) => {
    try {
      const brand = state.brands.find(b => b.id === brandId);
      if (!brand) {
        throw new Error(`Brand with ID ${brandId} not found`);
      }

      // Save to localStorage
      localStorage.setItem('currentBrand', JSON.stringify(brand));
      
      // Load brand-specific data
      loadBrandData(brand);
      
      // Update state
      setState(prev => ({ ...prev, currentBrand: brand }));
      
      console.log(`Brand switched to: ${brand.name}`);
    } catch (error) {
      console.error('Error setting brand by ID:', error);
      setState(prev => ({ ...prev, error: (error as Error).message }));
    }
  }, [state.brands, loadBrandData]);

  const setBrands = useCallback(async (brandIds: number[]) => {
    if (state.allBrands.length) {
      const brands = state.allBrands.filter(b => brandIds.includes(b.id));
      setState(prev => ({ ...prev, brands }));
      if (!brandIds.includes(state.currentBrand.id)) {
        setBrandById(brandIds[0]);
      }
    }
  }, [state.allBrands]);

  const getBrandFromLocalStorage = (): Brand | null => {
    try {
      const brandData = localStorage.getItem('currentBrand');
      if (!brandData) return null;
      const brand = Object.assign(new Brand(), JSON.parse(brandData));
      return brand;
    } catch (error) {
      console.error('Error getting brand from localStorage:', error);
      return null;
    }
  };
  
  // Get brand from domain (inline to avoid dependency)
  const getBrandFromDomain = (brands: Brand[]): Brand | null => {
    try {
      const domain = /:\/\/([^\/]+)/?.exec(window.location.href)?.[1]?.split(".") || [""];
      const domainName = domain[0];
      
      if (!domainName) return null;

      const domainToBrandMap: { [key: string]: string } = {
        'zuug': 'ZUUG',
        'alove': 'A-LOVE',
        'mujual': 'MUJUAL',
      };

      const brandName = domainToBrandMap[domainName];
      if (!brandName) return null;

      return brands.find(brand => 
        brand.name?.toLowerCase() === brandName.toLowerCase()
      ) || null;
    } catch (error) {
      console.error('Error getting brand from domain:', error);
      return null;
    }
  };
  

  // Initialize brands and set current brand
  const initializeBrands = useCallback(async () => {
    console.log('initializeBrands called, isInitialized:', isInitialized.current);
    
    // Prevent multiple initializations
    if (isInitialized.current) {
      console.log('Brands already initialized, skipping...');
      return;
    }
    
    isInitialized.current = true;
    console.log('Setting isInitialized to true');
    
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      
      // Get brand from localStorage (inline to avoid dependency)
      
      
      // Determine current brand (priority: existing brand > localStorage > domain > first brand)
      let currentBrand: Brand | null  = getBrandFromLocalStorage();
      
      // If no existing brand, try localStorage
       if(currentBrand){
        console.log('Brand from localStorage:', currentBrand?.name);
        setState(prev => ({ 
          ...prev, 
          currentBrand: currentBrand || new Brand(), 
          isLoading: false 
        }));
      }
        const brands = await getBrandsDO();
      
      // If not in localStorage, try domain
      if (!currentBrand) {
        // Fetch brands
        console.log(`Fetched ${brands.length} brands`);
        currentBrand = getBrandFromDomain(brands);
        console.log('Brand from domain:', currentBrand?.name);
      
        
        // If still no brand, use first available
        if (!currentBrand) {
          currentBrand = brands[0];
          console.log('Using first available brand:', currentBrand?.name);
        }
      }
      
      // If we have a current brand and it's different from existing, save it and load its data
      if (currentBrand) {
        console.log(`Setting new brand: ${currentBrand.name}`);
        localStorage.setItem('currentBrand', JSON.stringify(currentBrand));
        loadBrandData(currentBrand);
      } 
      
      // Update state once with all the data
      setState(prev => ({ 
        ...prev, 
        brands, 
        allBrands: brands,
        currentBrand: currentBrand || new Brand(), 
        isLoading: false 
      }));
      
      console.log(`Initialized brands. Current brand: ${currentBrand?.name || 'None'}`);
    } catch (error) {
      console.error('Error initializing brands:', error);
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: 'Failed to initialize brands' 
      }));
    }
  }, [loadBrandData]);

  return (
    <BrandsContext.Provider value={{
      ...state,
      setBrandById,
      
      initializeBrands,
      setBrands,
    }}>
      {children}
    </BrandsContext.Provider>
  );
} 